import type { MillsheetSortConfig } from '@mbfab/shared'
import { isPresent, sortStringsByConfig } from '@mbfab/shared'
import { Fragment, useState } from 'react'

import type { MillsheetFilterOptions, MillsheetFilterState } from './filter-state'

import { DateRangeField } from '@/components/ui/date-range-field'
import { FilterBar } from '@/components/ui/filter-bar'
import { FilterTextInput } from '@/components/ui/filter-text-input'
import { CompactFormField } from '@/components/ui/form'
import { MultiSelect } from '@/components/ui/multi-select'

// 工区・節・部位は登録時点で未入力のままになるケースがあるため、未設定のものを抽出できるようにする
// （v1 MillsheetFilterForm.tsx の emptyFilterableKeys と同じ）
const EMPTY_FILTERABLE_KEYS: Array<keyof MillsheetFilterState> = ['constructionArea', 'member', 'buildingPart']

// 文字列入力は空文字を「その項目では絞り込まない」の意味として扱う。同じ三項が 8 箇所に散ると
// 条件の向きがぶれるため 1 箇所に寄せる（否定条件を避けたい lint ルールへの対応も兼ねる）
function toContainsFilter(input: string): { contains: string } | undefined {
  return input === '' ? undefined : { contains: input }
}

// 複数選択はチェックボックス付きドロップダウン（ui/multi-select.tsx）で行う（2026/08/26 FB。
// ネイティブ <select multiple> は複数選択に ctrl/cmd+クリックが必要で操作方法が伝わらない）
function MultiSelectField({
  id,
  label,
  value,
  options,
  allowEmptyOption,
  onChange,
  testIdPrefix,
}: {
  id: string
  label: string
  value: string[]
  options: string[]
  allowEmptyOption?: boolean
  onChange: (value: string[]) => void
  testIdPrefix: string
}) {
  return (
    <CompactFormField id={id} label={label}>
      <MultiSelect
        id={id}
        label={label}
        values={value}
        options={[
          ...(allowEmptyOption === true ? [{ value: '', label: '（設定なし）' }] : []),
          ...options.map((option) => ({ value: option, label: option })),
        ]}
        onChange={onChange}
        testId={`${testIdPrefix}-${id}`}
      />
    </CompactFormField>
  )
}

function StringField({
  id,
  label,
  value,
  onChange,
  testIdPrefix,
}: {
  id: string
  label: string
  value: string
  onChange: (value: string) => void
  testIdPrefix: string
}) {
  return (
    <CompactFormField id={id} label={label}>
      <FilterTextInput
        id={id}
        type="text"
        value={value}
        onValueChange={onChange}
        data-testid={`${testIdPrefix}-${id}`}
        clearButtonTestId={`${testIdPrefix}-${id}-clear`}
      />
    </CompactFormField>
  )
}

// 適用中の絞り込み条件からチップを組み立てる（order-matching/filter-bar.tsx と同じUI）。
// 複数選択の値は項目を分けず、1チップにカンマ区切りでまとめて表示する（2026/08/26 FB）
type Chip = { key: keyof MillsheetFilterState | 'freeWord'; label: string; value: string }

type ParentFilterKey =
  | 'no'
  | 'constructionName'
  | 'constructionArea'
  | 'member'
  | 'buildingPart'
  | 'manufacturerName'
  | 'tradingCompany'
  | 'issuedDate'
  | 'orderNumber'
  | 'innerNo'
  | 'customer'
  | 'submissionDate'

const PARENT_FILTER_ORDER: Record<'millsheet' | 'unmatched', ParentFilterKey[]> = {
  millsheet: [
    'no',
    'constructionName',
    'constructionArea',
    'member',
    'buildingPart',
    'manufacturerName',
    'tradingCompany',
    'issuedDate',
    'orderNumber',
    'innerNo',
    'customer',
    'submissionDate',
  ],
  unmatched: [
    'no',
    'orderNumber',
    'constructionName',
    'tradingCompany',
    'manufacturerName',
    'issuedDate',
    'customer',
    'constructionArea',
    'member',
    'buildingPart',
    'innerNo',
    'submissionDate',
  ],
}

function buildChips(value: MillsheetFilterState, freeWord: string): Chip[] {
  const chips: Chip[] = []
  if (isPresent(freeWord.trim())) {
    chips.push({ key: 'freeWord', label: 'フリーワード', value: freeWord.trim() })
  }
  const pushIn = (key: keyof MillsheetFilterState, label: string, values: string[] | undefined) => {
    if (isPresent(values) && values.length > 0) {
      chips.push({ key, label, value: values.map((v) => (v === '' ? '（設定なし）' : v)).join(', ') })
    }
  }
  const pushContains = (key: keyof MillsheetFilterState, label: string, text: string | undefined) => {
    if (isPresent(text) && text !== '') {
      chips.push({ key, label, value: text })
    }
  }
  const pushRange = (
    key: keyof MillsheetFilterState,
    label: string,
    gte: string | undefined,
    lte: string | undefined,
  ) => {
    if (isPresent(gte) || isPresent(lte)) {
      chips.push({ key, label, value: `${gte?.slice(0, 10) ?? ''} 〜 ${lte?.slice(0, 10) ?? ''}` })
    }
  }

  pushIn('manufacturerName', 'メーカー', value.manufacturerName?.in)
  pushIn('tradingCompany', '商社', value.tradingCompany?.in)
  pushIn('customer', '需要家', value.customer?.in)
  pushIn('constructionName', '工事名称', value.constructionName?.in)
  pushIn('constructionArea', '工区（明細）', value.constructionArea?.in)
  pushIn('member', '節（明細）', value.member?.in)
  pushIn('buildingPart', '部位（明細）', value.buildingPart?.in)
  pushRange('issuedDate', '発行日', value.issuedDate?.gte, value.issuedDate?.lte)
  pushIn('submissionDate', '提出日', value.submissionDate?.in)
  pushContains('no', '証明書番号', value.no?.contains)
  pushContains('orderNumber', '注文番号', value.orderNumber?.contains)
  pushContains('innerNo', '内部番号', value.innerNo?.contains)
  pushIn('steelSteelType', '規格（明細）', value.steelSteelType?.in)
  pushIn('steelSectionType', '形状（明細）', value.steelSectionType?.in)
  pushContains('steelSize', '寸法（明細）', value.steelSize?.contains)
  pushContains('steelLength', '長さ（明細）', value.steelLength?.contains)
  pushContains('steelHeatNumber', '製鋼番号（明細）', value.steelHeatNumber?.contains)
  pushContains('steelIndividualNumber', '個体番号（明細）', value.steelIndividualNumber?.contains)
  pushIn('steelEndorsementCompany', '裏書会社（明細）', value.steelEndorsementCompany?.in)
  pushRange('steelEndorsementDate', '裏書日（明細）', value.steelEndorsementDate?.gte, value.steelEndorsementDate?.lte)

  return chips
}

export function MillsheetFilterBar({
  value,
  onChange,
  freeWord,
  onFreeWordChange,
  onPendingChange,
  onClearAll,
  options,
  sortConfig,
  testIdPrefix = 'millsheet-filter',
  freeWordPlaceholder = 'フリーワードで検索（証明書番号・工事名称・メーカーなど）',
  millsheetFilterHeading = 'ミルシートを絞り込み',
  detailFilterHeading = '明細を絞り込み',
  variant = 'millsheet',
}: {
  value: MillsheetFilterState
  onChange: (next: MillsheetFilterState) => void
  // 適用済みのフリーワード。入力中の文字列は FilterBar が持つ
  freeWord: string
  onFreeWordChange: (next: string) => void
  onPendingChange?: (pending: boolean) => void
  // 「すべてクリア」は詳細条件とフリーワードを**同時に**空へ戻す操作なので、専用のコールバックにする。
  // onChange と onFreeWordChange を続けて呼ぶ実装にしてはいけない:
  // 呼び出し側が両者を1つの状態（例: millsheet-workspace の onChangeSearch）へ合流させている場合、
  // 2回目の呼び出しがレンダー時のクロージャに閉じ込められた**古い方の値**を一緒に送ってしまい、
  // 直前にクリアしたはずの条件が復活する（実際に一覧が絞り込まれたまま戻らない不具合になっていた）
  onClearAll: () => void
  options: MillsheetFilterOptions
  sortConfig: MillsheetSortConfig
  testIdPrefix?: string
  freeWordPlaceholder?: string
  millsheetFilterHeading?: string
  detailFilterHeading?: string
  variant?: 'millsheet' | 'unmatched'
}) {
  const [expanded, setExpanded] = useState(false)

  const chips = buildChips(value, freeWord)
  // 詳細絞り込みボタンのバッジはフリーワードを除いた詳細条件の数（order-matching/filter-bar.tsx と同じ）
  const detailChipCount = chips.filter((chip) => chip.key !== 'freeWord').length

  const removeChip = (key: Chip['key']) => {
    if (key === 'freeWord') {
      onFreeWordChange('')
      return
    }
    onChange({ ...value, [key]: undefined })
  }

  const parentFields: Record<ParentFilterKey, React.ReactNode> = {
    no: (
      <StringField
        id="no"
        testIdPrefix={testIdPrefix}
        label="証明書番号"
        value={value.no?.contains ?? ''}
        onChange={(next) => {
          onChange({ ...value, no: toContainsFilter(next) })
        }}
      />
    ),
    constructionName: (
      <MultiSelectField
        id="constructionName"
        testIdPrefix={testIdPrefix}
        label="工事名称"
        value={value.constructionName?.in ?? []}
        options={sortStringsByConfig(options.constructionNames, undefined)}
        onChange={(next) => {
          onChange({ ...value, constructionName: next.length > 0 ? { in: next } : undefined })
        }}
      />
    ),
    constructionArea: (
      <MultiSelectField
        id="constructionArea"
        testIdPrefix={testIdPrefix}
        label="工区（明細）"
        value={value.constructionArea?.in ?? []}
        options={sortStringsByConfig(options.constructionAreas, undefined)}
        allowEmptyOption={EMPTY_FILTERABLE_KEYS.includes('constructionArea')}
        onChange={(next) => {
          onChange({ ...value, constructionArea: next.length > 0 ? { in: next } : undefined })
        }}
      />
    ),
    member: (
      <MultiSelectField
        id="member"
        testIdPrefix={testIdPrefix}
        label="節（明細）"
        value={value.member?.in ?? []}
        options={sortStringsByConfig(options.members, undefined)}
        allowEmptyOption={EMPTY_FILTERABLE_KEYS.includes('member')}
        onChange={(next) => {
          onChange({ ...value, member: next.length > 0 ? { in: next } : undefined })
        }}
      />
    ),
    buildingPart: (
      <MultiSelectField
        id="buildingPart"
        testIdPrefix={testIdPrefix}
        label="部位（明細）"
        value={value.buildingPart?.in ?? []}
        options={sortStringsByConfig(options.buildingParts, undefined)}
        allowEmptyOption={EMPTY_FILTERABLE_KEYS.includes('buildingPart')}
        onChange={(next) => {
          onChange({ ...value, buildingPart: next.length > 0 ? { in: next } : undefined })
        }}
      />
    ),
    manufacturerName: (
      <MultiSelectField
        id="manufacturerName"
        label="メーカー"
        value={value.manufacturerName?.in ?? []}
        options={sortStringsByConfig(options.manufacturers, sortConfig.manufacturerName)}
        onChange={(next) => {
          onChange({ ...value, manufacturerName: next.length > 0 ? { in: next } : undefined })
        }}
        testIdPrefix={testIdPrefix}
      />
    ),
    tradingCompany: (
      <MultiSelectField
        id="tradingCompany"
        testIdPrefix={testIdPrefix}
        label="商社"
        value={value.tradingCompany?.in ?? []}
        options={sortStringsByConfig(options.tradingCompanies, undefined)}
        onChange={(next) => {
          onChange({ ...value, tradingCompany: next.length > 0 ? { in: next } : undefined })
        }}
      />
    ),
    issuedDate: (
      <DateRangeField
        idPrefix="issuedDate"
        testIdPrefix={testIdPrefix}
        label="発行日"
        gte={value.issuedDate?.gte}
        lte={value.issuedDate?.lte}
        onChange={({ gte, lte }) => {
          onChange({ ...value, issuedDate: isPresent(gte) || isPresent(lte) ? { gte, lte } : undefined })
        }}
      />
    ),
    orderNumber: (
      <StringField
        id="orderNumber"
        label="注文番号"
        value={value.orderNumber?.contains ?? ''}
        onChange={(next) => {
          onChange({ ...value, orderNumber: toContainsFilter(next) })
        }}
        testIdPrefix={testIdPrefix}
      />
    ),
    innerNo: (
      <StringField
        id="innerNo"
        testIdPrefix={testIdPrefix}
        label="内部番号"
        value={value.innerNo?.contains ?? ''}
        onChange={(next) => {
          onChange({ ...value, innerNo: toContainsFilter(next) })
        }}
      />
    ),
    customer: (
      <MultiSelectField
        id="customer"
        testIdPrefix={testIdPrefix}
        label="需要家"
        value={value.customer?.in ?? []}
        options={sortStringsByConfig(options.customers, undefined)}
        onChange={(next) => {
          onChange({ ...value, customer: next.length > 0 ? { in: next } : undefined })
        }}
      />
    ),
    submissionDate: (
      <MultiSelectField
        id="submissionDate"
        testIdPrefix={testIdPrefix}
        label="提出日"
        value={value.submissionDate?.in ?? []}
        options={options.submissionDates}
        allowEmptyOption
        onChange={(next) => {
          onChange({ ...value, submissionDate: next.length > 0 ? { in: next } : undefined })
        }}
      />
    ),
  }

  return (
    <FilterBar
      freeWord={freeWord}
      onFreeWordChange={onFreeWordChange}
      onPendingChange={onPendingChange}
      freeWordPlaceholder={freeWordPlaceholder}
      expanded={expanded}
      onExpandedChange={setExpanded}
      chips={chips}
      detailFilterCount={detailChipCount}
      onRemoveChip={removeChip}
      onClearAll={onClearAll}
      testIdPrefix={testIdPrefix}
    >
      <div className="space-y-6">
        <div>
          <h3 className="mb-3 text-sm font-semibold text-foreground">{millsheetFilterHeading}</h3>
          <div className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2 lg:grid-cols-4">
            {PARENT_FILTER_ORDER[variant].map((key) => (
              <Fragment key={key}>{parentFields[key]}</Fragment>
            ))}
          </div>
        </div>

        <div>
          <h3 className="mb-3 text-sm font-semibold text-foreground">{detailFilterHeading}</h3>
          <div className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2 lg:grid-cols-4">
            <MultiSelectField
              id="steelSteelType"
              testIdPrefix={testIdPrefix}
              label="規格"
              value={value.steelSteelType?.in ?? []}
              options={sortStringsByConfig(options.steelTypes, sortConfig.steelType)}
              onChange={(next) => {
                onChange({ ...value, steelSteelType: next.length > 0 ? { in: next } : undefined })
              }}
            />
            <MultiSelectField
              id="steelSectionType"
              testIdPrefix={testIdPrefix}
              label="形状"
              value={value.steelSectionType?.in ?? []}
              options={sortStringsByConfig(options.steelSectionTypes, sortConfig.sectionType)}
              onChange={(next) => {
                onChange({ ...value, steelSectionType: next.length > 0 ? { in: next } : undefined })
              }}
            />
            <StringField
              id="steelSize"
              testIdPrefix={testIdPrefix}
              label="寸法"
              value={value.steelSize?.contains ?? ''}
              onChange={(next) => {
                onChange({ ...value, steelSize: toContainsFilter(next) })
              }}
            />
            <StringField
              id="steelLength"
              testIdPrefix={testIdPrefix}
              label="長さ"
              value={value.steelLength?.contains ?? ''}
              onChange={(next) => {
                onChange({ ...value, steelLength: toContainsFilter(next) })
              }}
            />
            <StringField
              id="steelHeatNumber"
              testIdPrefix={testIdPrefix}
              label="製鋼番号"
              value={value.steelHeatNumber?.contains ?? ''}
              onChange={(next) => {
                onChange({ ...value, steelHeatNumber: toContainsFilter(next) })
              }}
            />
            <StringField
              id="steelIndividualNumber"
              testIdPrefix={testIdPrefix}
              label="個体番号"
              value={value.steelIndividualNumber?.contains ?? ''}
              onChange={(next) => {
                onChange({ ...value, steelIndividualNumber: toContainsFilter(next) })
              }}
            />
            <MultiSelectField
              id="steelEndorsementCompany"
              testIdPrefix={testIdPrefix}
              label="裏書会社"
              value={value.steelEndorsementCompany?.in ?? []}
              options={sortStringsByConfig(options.endorsementCompanies, undefined)}
              onChange={(next) => {
                onChange({ ...value, steelEndorsementCompany: next.length > 0 ? { in: next } : undefined })
              }}
            />
            <DateRangeField
              idPrefix="steelEndorsementDate"
              testIdPrefix={testIdPrefix}
              label="裏書日"
              gte={value.steelEndorsementDate?.gte}
              lte={value.steelEndorsementDate?.lte}
              onChange={({ gte, lte }) => {
                onChange({
                  ...value,
                  steelEndorsementDate: isPresent(gte) || isPresent(lte) ? { gte, lte } : undefined,
                })
              }}
            />
          </div>
        </div>
      </div>
    </FilterBar>
  )
}
